/**
 * **The Ankiety surfaces' whole read, narrowed.** The one place in this feature that knows a
 * generated DTO exists — `ui/` and `model/` may not import `generated/*` at all, not even type-only.
 *
 * Promoted out of `pages/client-detail/api/clientDetailSource` on 17 September 2026, when `/raporty`
 * became the second surface that draws one occurrence's report. `toLatestSurvey` stayed there: it
 * answers the Przegląd card's question — the newest completed survey and its three tiles — off the
 * same read. Two projections of one response, because the two surfaces need different things and a
 * single mapper serving both would carry every field either one has ever wanted.
 *
 * Every field is narrowed here: nullable to nullable-with-a-meaning, wire enum to the app's own
 * word. Almost every field on every survey DTO is optional, because orval emits the OpenAPI document
 * faithfully and the document marks nothing required — so the guards are `== null` throughout, which
 * covers a read that has not landed and a body that arrived as null in one test.
 *
 * ── An INTAKE has no cycle, and is dated by when it ARRIVED ──────────────────────────────────
 * `GET /customer/{id}/surveys` sends `cycleDate: null` for every intake, because an intake is asked
 * once and stands for no cycle. Everything downstream is positional — the ordinal, the month a
 * stream row is filed under, the x domain, the delta chain — so an occurrence with no date is
 * dropped by `buildSurveyHistory` and by `buildSurveyStream` alike, and until 17 September 2026 that
 * meant **the intake was unreachable on this app**: invisible in the client's Ankiety stream, and
 * „Podgląd" on the mandatory form said the submission was gone. It went unnoticed because the e2e
 * fixture gave its intake a `cycleDate` the backend has never sent.
 *
 * So an undated occurrence is dated HERE, at the seam, by the day it was completed — which is the
 * rule `pages/reports/model/reviewQueue` already states for the same fact („a check-in belongs to a
 * cycle, an intake is dated by when it arrived"). The instant is read into the READER's calendar
 * (`toIsoDate`) rather than sliced out of the UTC string, so a form finished at half past midnight
 * in Warsaw is filed under the day the trainer would call it. An occurrence with neither a cycle nor
 * a completion is still undated, and is still dropped: there is nothing to report about it.
 */

import { toIsoDate } from '~/shared/lib/calendarDate';
import type {
  TrainWithMeModulesSurveysDtosResponsesCustomerSurveysResponse as SurveysDto,
  TrainWithMeModulesSurveysDtosSurveyPhotoAnswer as SurveyPhotoDto,
} from '~/shared/api/generated/model';

import type {
  SurveyAnswerType,
  SurveyCadenceFrequency,
  SurveyHistorySource,
  SurveyOccurrenceStatus,
  SurveyPhotoPose,
  SurveyPhotoSource,
} from '../model/surveyHistory';

/** The wire's `AnswerType` words, narrowed. Anything else is `null` and the row prints its text. */
const ANSWER_TYPES: readonly SurveyAnswerType[] = [
  'ShortText',
  'LongText',
  'SingleChoice',
  'MultiChoice',
  'Number',
  'Scale',
  'Date',
  'GoalBuilder',
  'Measurements',
  'Photo',
  'ExerciseClip',
];

function answerTypeOf(wire: string | null | undefined): SurveyAnswerType | null {
  return ANSWER_TYPES.find((type) => type === wire) ?? null;
}

const OCCURRENCE_STATUSES: readonly SurveyOccurrenceStatus[] = [
  'OnTime',
  'Late',
  'Missed',
  'Cancelled',
];

const CADENCE_FREQUENCIES: readonly SurveyCadenceFrequency[] = ['Day', 'Week', 'Month'];

/**
 * The four photo slots, as the answer carries them: a media id per pose, resolved through the
 * assignment's own `photoUrls` table.
 *
 * A pose with an id but no signed URL keeps its place with a `null` url rather than disappearing —
 * the projection decides what an unpublished photo looks like, and losing the pose here would make
 * "we have no back shot" and "the back shot's URL expired" the same thing.
 */
function toSurveyPhotos(
  photos: SurveyPhotoDto | null | undefined,
  urls: Readonly<Record<string, string>> | null | undefined,
): readonly SurveyPhotoSource[] {
  if (photos == null) return [];
  const slots: readonly { pose: SurveyPhotoPose; mediaId: number | null | undefined }[] = [
    { pose: 'front', mediaId: photos.front },
    { pose: 'side', mediaId: photos.side },
    { pose: 'secondSide', mediaId: photos.secondSide },
    { pose: 'back', mediaId: photos.back },
  ];
  return slots.flatMap((slot) =>
    slot.mediaId == null
      ? []
      : [{ pose: slot.pose, url: urls?.[String(slot.mediaId)] ?? null }],
  );
}

/**
 * The civil date an occurrence stands for: its cycle, or — for an intake, which has none — the day
 * it was completed, in the reader's own calendar. See the header.
 */
function occurrenceDate(
  cycleDate: string | null | undefined,
  completedAt: string | null | undefined,
): string | null {
  if (cycleDate != null) return cycleDate;
  if (completedAt == null) return null;
  const at = new Date(completedAt);
  return Number.isNaN(at.getTime()) ? null : toIsoDate(at);
}

export function toSurveyHistorySource(dto: SurveysDto | undefined): SurveyHistorySource | null {
  if (dto?.surveys == null) return null;

  const occurrences = dto.surveys.flatMap((survey) => {
    if (survey.id == null) return [];
    const urls = survey.photoUrls ?? null;
    return [
      {
        id: survey.id,
        templateId: survey.surveyTemplateId ?? null,
        title: survey.surveyTitle ?? '',
        kind: survey.kind ?? null,
        frequency:
          CADENCE_FREQUENCIES.find((entry) => entry === survey.cadence?.frequency) ?? null,
        cycleDate: occurrenceDate(survey.cycleDate, survey.completedAt),
        sentAt: survey.sentAt ?? null,
        completedAt: survey.completedAt ?? null,
        status: survey.status ?? null,
        occurrenceStatus:
          OCCURRENCE_STATUSES.find((entry) => entry === survey.occurrenceStatus) ?? null,
        reviewedAt: survey.reviewedAt ?? null,
        photoUrlsExpireAt: survey.photoUrlsExpireAt ?? null,
        questions: (survey.questions ?? []).flatMap((question) => {
          // The key is the join to every answer. A question without one cannot be addressed, so it
          // is dropped rather than given a synthetic id nothing else would agree with.
          if (question.key == null) return [];
          return [
            {
              key: question.key,
              // The STANDARD catalog's own code, when this is a catalog question. It is what the
              // label is resolved from — see `model/surveyHistory`'s `questionLabels` — and `null`
              // on a question the trainer wrote themselves.
              code: question.code ?? null,
              // The trainer's own wording. An empty label would leave an unnamed row, so the
              // catalog code stands in — it is at least the question's identity.
              label: question.label ?? question.code ?? question.key,
              answerType: answerTypeOf(question.answerType),
              options: question.options ?? [],
              exerciseId: question.exerciseId ?? null,
              vitalType: question.vitalType ?? null,
              // Absent reads as OPTIONAL. The backend sends the flag on every frozen snapshot; a
              // body that somehow omits it must not put a lock on a question nobody had to answer.
              required: question.required === true,
            },
          ];
        }),
        answers: (survey.answers ?? []).flatMap((answer) => {
          if (answer.key == null) return [];
          const measurements = answer.measurements ?? null;
          return [
            {
              key: answer.key,
              number: answer.number ?? null,
              text: answer.text ?? null,
              choices: answer.choices ?? [],
              measurements:
                measurements === null
                  ? null
                  : {
                      // `weightKg` is the one non-nullable field on the wire's measurement request,
                      // and it still arrives absent on a partial answer — so it is read like the
                      // other ten rather than trusted.
                      weightKg: measurements.weightKg ?? null,
                      bodyFatPercentage: measurements.bodyFatPercentage ?? null,
                      neckCm: measurements.neckCm ?? null,
                      shoulderCm: measurements.shoulderCm ?? null,
                      chestCm: measurements.chestCm ?? null,
                      bicepCm: measurements.bicepCm ?? null,
                      forearmCm: measurements.forearmCm ?? null,
                      waistCm: measurements.waistCm ?? null,
                      hipCm: measurements.hipCm ?? null,
                      thighCm: measurements.thighCm ?? null,
                      calfCm: measurements.calfCm ?? null,
                    },
              photos: toSurveyPhotos(answer.photos, urls),
              clipId: answer.clipId ?? null,
              goal:
                answer.goal == null
                  ? null
                  : {
                      type: answer.goal.type,
                      targetValue: answer.goal.targetValue ?? null,
                      currentValue: answer.goal.currentValue ?? null,
                      deadline: answer.goal.deadline ?? null,
                      title: answer.goal.title ?? null,
                      description: answer.goal.description ?? null,
                      exerciseId: answer.goal.exerciseId ?? null,
                      periodDays: answer.goal.periodDays ?? null,
                    },
            },
          ];
        }),
        clips: new Map(
          Object.entries(survey.clipAnswers ?? {}).flatMap(([clipId, entry]) => {
            const id = Number(clipId);
            if (!Number.isFinite(id)) return [];
            const media = entry.clip?.media ?? null;
            return [
              [
                id,
                {
                  // `Expired` is a first-class answer, not a missing clip: the trainer is told the
                  // recording aged out rather than shown a player that cannot play.
                  status: entry.status === 'Expired' ? ('Expired' as const) : ('Available' as const),
                  videoUrl: media?.videoUrl ?? null,
                  posterUrl: media?.posterUrl ?? null,
                  durationSeconds: media?.durationSeconds ?? null,
                  // The CLIP's own thirty-day expiry, not `media.urlExpiresAt` — that one is the
                  // signature on the URL and lapses in about twenty minutes, which would print a
                  // date this afternoon under a recording that is good for another month.
                  expiresAt: entry.clip?.expiresAt ?? null,
                },
              ] as const,
            ];
          }),
        ),
      },
    ];
  });

  return {
    // Ascending by the CYCLE each occurrence stands for — the wire's own order is `SentAt`
    // descending. `model/surveyHistory`'s `orderedOccurrences` applies the same total order and
    // carries the reasoning; the case is pinned by
    // `aLateOccurrenceStaysInItsOwnCyclesPlaceRatherThanJumpingAheadOfTheNextOne`.
    occurrences: [...occurrences].sort(
      (left, right) =>
        (left.cycleDate ?? left.completedAt ?? '').localeCompare(
          right.cycleDate ?? right.completedAt ?? '',
        ) ||
        (left.completedAt ?? '').localeCompare(right.completedAt ?? '') ||
        left.id - right.id,
    ),
    isTruncated: dto.isTruncated === true,
  };
}
